import type { ReactNode } from "react";
import { pageMeta } from "../../pages/pageMetadata";
import { siteDesignAttributes, type SiteDesign } from "../../themes";
import { BLOCK_FLOW_CLASS } from "../document/blockFlow";
import type { EditorAsset, EditorBlock, EditorPage } from "../editor.types";
import { assetService } from "../../assets";
import { SiteBlockList } from "./SiteBlockList";
import { SiteNavigation } from "./SiteNavigation";

type SitePageViewProps = {
  design: SiteDesign;
  siteName: string;
  siteDescription: string | null;
  /** The site's pages; the navigation shows those in its menu. */
  pages: EditorPage[];
  currentPage: EditorPage | undefined;
  /** The page's blocks, or undefined while they load. */
  blocks: EditorBlock[] | undefined;
  blocksError?: string;
  assetsById: Map<string, EditorAsset>;
  href: (page: EditorPage) => string;
  onNavigate: (page: EditorPage) => void;
  /** Shown in place of the article when there is no current page. */
  notFound: ReactNode;
  className?: string;
};

/**
 * A page of the site as visitors see it: the themed navigation, the page's
 * title and introduction, and its blocks. The live site and the editor's
 * Preview both render through this, so a preview matches what publishes.
 */
export function SitePageView({
  design,
  siteName,
  siteDescription,
  pages,
  currentPage,
  blocks,
  blocksError,
  assetsById,
  href,
  onNavigate,
  notFound,
  className = "min-h-screen",
}: SitePageViewProps) {
  const rootBlocks = (blocks ?? []).filter((block) => block.parent_source_block_id === null);
  const logo = design.logo ? assetsById.get(design.logo) : undefined;
  return (
    <main
      {...siteDesignAttributes(design)}
      className={`@container overflow-x-clip bg-site-bg font-site-body text-site-text ${className}`}
    >
      <div className="mx-auto max-w-4xl">
        <SiteNavigation
          design={design}
          name={siteName}
          logoUrl={logo ? assetService.getPublicUrl(logo.storage_path) : null}
          pages={pages}
          currentPageId={currentPage?.id}
          href={href}
          onNavigate={onNavigate}
        />
      </div>

      {currentPage ? (
        <article className="mx-auto max-w-4xl px-5 py-8 sm:px-8 sm:py-12">
          {!currentPage.is_home && (
            <header className="site-page-header border-b border-site-border pb-7">
              <h1 className="site-heading leading-tight heading-size-36 sm:heading-size-60">
                {currentPage.title}
              </h1>
              {!pageMeta(currentPage).descriptionInBlocks && currentPage.description && (
                <p className="mt-4 max-w-2xl text-base leading-7 text-site-muted">
                  {currentPage.description}
                </p>
              )}
            </header>
          )}
          {currentPage.is_home && siteDescription && (
            <p className="text-base leading-7 text-site-muted">{siteDescription}</p>
          )}

          <div className={`${BLOCK_FLOW_CLASS} py-7`}>
            {blocks === undefined ? (
              blocksError ? (
                <p
                  className="rounded-site border border-site-border bg-site-highlight px-4 py-3 text-sm font-semibold text-hazumu-danger"
                  role="alert"
                >
                  {blocksError}
                </p>
              ) : (
                <p className="text-sm text-site-muted">Loading page…</p>
              )
            ) : rootBlocks.length ? (
              <SiteBlockList
                blocks={blocks}
                parent={null}
                assetsById={assetsById}
                currentPageId={currentPage.id}
                onOpenPage={onNavigate}
                onSelectPage={(pageId) => {
                  const target = pages.find((page) => page.id === pageId);
                  if (target) onNavigate(target);
                }}
                pageHref={href}
                pages={pages}
              />
            ) : (
              <p className="text-sm text-site-muted">This page is empty.</p>
            )}
          </div>
        </article>
      ) : (
        notFound
      )}
    </main>
  );
}
